import { useCallback, useEffect, useRef, useState } from 'react';

import { appendTestableComponentAttributes } from '@dreryk/react-components';
import { useTranslate } from '@dreryk/react-common';

import { translationKeys } from '../translations';

import { StyledVisualizerBar, StyledVisualizerContainer } from './VoiceVisualizer.styled';

const BAR_COUNT = 16;
const FFT_SIZE = 64;
const MIN_HEIGHT = 3;
const MAX_HEIGHT = 44;
const BYTE_MAX_VALUE = 255;

function createInitialBarHeights(): number[] {
   return Array.from({ length: BAR_COUNT }, () => MIN_HEIGHT);
}

function mapByteToHeight(value: number): number {
   return MIN_HEIGHT + (value / BYTE_MAX_VALUE) * (MAX_HEIGHT - MIN_HEIGHT);
}

type VoiceVisualizerProps = {
   mediaStream: MediaStream | null;
   isActive: boolean;
};

type AudioAnalysisRefs = {
   audioContextRef: React.MutableRefObject<AudioContext | null>;
   analyserRef: React.MutableRefObject<AnalyserNode | null>;
   sourceRef: React.MutableRefObject<MediaStreamAudioSourceNode | null>;
   animationFrameRef: React.MutableRefObject<number | null>;
};

function startAudioAnalysis(
   mediaStream: MediaStream,
   refs: AudioAnalysisRefs,
   setBarHeights: (heights: number[]) => void
): void {
   const audioContext = new AudioContext();
   refs.audioContextRef.current = audioContext;

   const analyser = audioContext.createAnalyser();
   analyser.fftSize = FFT_SIZE;
   refs.analyserRef.current = analyser;

   const source = audioContext.createMediaStreamSource(mediaStream);
   source.connect(analyser);
   refs.sourceRef.current = source;

   const { frequencyBinCount } = analyser;
   const dataArray = new Uint8Array(frequencyBinCount);

   function animate(): void {
      if (refs.analyserRef.current === null) {
         return;
      }
      refs.analyserRef.current.getByteFrequencyData(dataArray);
      const step = frequencyBinCount / BAR_COUNT;
      const heights: number[] = [];
      for (let i = 0; i < BAR_COUNT; i++) {
         const index = Math.floor(i * step);
         heights.push(mapByteToHeight(dataArray[index]));
      }
      setBarHeights(heights);
      refs.animationFrameRef.current = requestAnimationFrame(animate);
   }

   if (audioContext.state === 'suspended') {
      audioContext.resume().then(function onResumed(): void {
         refs.animationFrameRef.current = requestAnimationFrame(animate);
      });
   } else {
      refs.animationFrameRef.current = requestAnimationFrame(animate);
   }
}

function stopAudioAnalysis(refs: AudioAnalysisRefs): void {
   if (refs.animationFrameRef.current !== null) {
      cancelAnimationFrame(refs.animationFrameRef.current);
      refs.animationFrameRef.current = null;
   }
   if (refs.sourceRef.current !== null) {
      refs.sourceRef.current.disconnect();
      refs.sourceRef.current = null;
   }
   if (refs.analyserRef.current !== null) {
      refs.analyserRef.current.disconnect();
      refs.analyserRef.current = null;
   }
   if (refs.audioContextRef.current !== null) {
      refs.audioContextRef.current.close();
      refs.audioContextRef.current = null;
   }
}

export const VoiceVisualizer = (props: VoiceVisualizerProps): JSX.Element => {
   const translate = useTranslate();
   const [barHeights, setBarHeights] = useState<number[]>(createInitialBarHeights);

   const audioContextRef = useRef<AudioContext | null>(null);
   const analyserRef = useRef<AnalyserNode | null>(null);
   const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
   const animationFrameRef = useRef<number | null>(null);

   const refsRef = useRef<AudioAnalysisRefs>({
      audioContextRef: audioContextRef,
      analyserRef: analyserRef,
      sourceRef: sourceRef,
      animationFrameRef: animationFrameRef,
   });

   const cleanup = useCallback(function cleanup(): void {
      stopAudioAnalysis(refsRef.current);
   }, []);

   useEffect(
      function setupAudioAnalysis(): VoidFunction | undefined {
         if (!props.isActive || props.mediaStream === null) {
            cleanup();
            setBarHeights(createInitialBarHeights());
            return undefined;
         }

         startAudioAnalysis(props.mediaStream, refsRef.current, setBarHeights);

         return function cleanupEffect(): void {
            cleanup();
         };
      },
      [props.mediaStream, props.isActive, cleanup]
   );

   const statusText = props.isActive
      ? translate(translationKeys.voiceVisualizer.active)
      : translate(translationKeys.voiceVisualizer.inactive);

   return (
      <StyledVisualizerContainer
         {...appendTestableComponentAttributes('VoiceVisualizer', 'voiceVisualizer')}
         role='img'
         aria-label={translate(translationKeys.voiceVisualizer.label)}
      >
         <span
            aria-live='polite'
            className='sr-only'
            style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)' }}
         >
            {statusText}
         </span>
         {barHeights.map(function renderBar(height: number, index: number): JSX.Element {
            /*
             * Bars are purely visual, derived from a fixed-length array —
             * index is the correct stable key here.
             */
            // eslint-disable-next-line react/no-array-index-key
            return <StyledVisualizerBar key={index} $height={height} aria-hidden='true' />;
         })}
      </StyledVisualizerContainer>
   );
};
